<template>
  <fts-design-modal
    class="fts-action-design-modal"
    ref="ftsDesignModal">
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <fts-emoji v-model="formData.emoji" :toggle-size="18" class="design-modal-header-emoji" />
        <input 
          type="text" 
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{error: $v.formData.name && $v.formData.name.$error}" 
          :style="getStyle"
          v-model="formData.name"
          spellcheck="false"
          ref="designerName">
        <!-- color -->
        <swatches 
          class="design-modal-header-swatches"
          v-model="formData.color"
          row-length="7"
          :swatches="colors"
          shapes="circles"
          popover-x="right"
          :swatch-size="25"
          background-color="var(--fts-popover-alt-background)"
          @input="selectColor"  
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">
        {{error('name')}}
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Custom</span>
      <span class="design-modal-header-description">
        special actions
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- listing -->
      <fts-form-element
        :formData="formData"
        :item="{
          fieldName: 'menus',
          displayName: 'Listing',
          icon: 'fts-icon-menu-caret-down',
          helper: 'To restrict access to specific listings for this custom action.'
        }"
        comfort-design
        class="fts-col">
        <fts-select
          slot="formElement"
          slot-scope="{ formData, item }"
          :form-data="formData"
          :item="item"
          :options="menus"
          comfort-design
          multiple
        >
          <fts-label-group
            slot="selected-option-container"
            slot-scope="{ option }"
            :item-id="option.id"
            :list="menus"
            emoji
          />
          <fts-label-group
            slot="option"
            slot-scope="{ option }"
            :item-id="option.id"
            :list="menus"
            emoji
          />
        </fts-select>
      </fts-form-element>
      <!-- is multiple -->
      <fts-form-element
        :item="{
          fieldName: 'isMultiple',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <!-- is default -->
      <fts-form-element
        :item="{
          fieldName: 'isDefault',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
        v-if="isInternal"
      >
        <template #formLabel="{ label }">
          {{label}} <fts-release-badge alpha class="fts-align-top" />
        </template>
      </fts-form-element>
      <!-- is list only -->
      <fts-form-element
        :item="{
          fieldName: 'isListOnly',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
        v-if="isInternal"
      >
        <template #formLabel="{ label }">
          {{label}} (deprecated) <fts-release-badge alpha class="fts-align-top" />
        </template>
      </fts-form-element>
      <!-- is menu -->
      <fts-form-element
        :item="{
          fieldName: 'isMenu',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <div class="comfort-form-design">
        <!-- script -->
        <fts-collapsible-element
          :condition="!!formData.script"
          label="script"
          immersive
          beta
          class="fts-form-group"
        >
          <fts-script-editor
            slot="content"
            editor-id="script"
            :save-button="saveCallBack"
            v-model="formData.script"
            lang="javascript"
            label="Script"
            disabled-dictionary
            @save="done(true, true)"
          >
            <template #label="{ label }">
              {{label}} <fts-release-badge class="fts-align-top" />
            </template>
          </fts-script-editor>
        </fts-collapsible-element>
        <!-- condition -->
        <fts-collapsible-element
          :condition="!!formData.condition"
          label="condition script"
          immersive
          beta
          class="fts-form-group"
        >
          <fts-script-editor
            slot="content"
            editor-id="conditionScript"
            :save-button="saveCallBack"
            v-model="formData.condition"
            lang="javascript"
            label="Condition"
            disabled-dictionary
            :disabled="conditionDisabled"
            @save="done(true, true)"
          >
            <template #label="{ label }">
              {{label}} <fts-release-badge class="fts-align-top" />
            </template>
          </fts-script-editor>
        </fts-collapsible-element>
      </div>
      <template v-if="isInternal">
        <!-- type -->
        <fts-form-element
          :form-data="formData"
          :item="{
            'fieldName': 'type',
            'displayName': 'Type',
            'icon': 'fts-icon-menu-caret-down',
          }"
          comfort-design
        >
          <template #formLabel="{ label }">
            {{label}} <fts-release-badge alpha class="fts-align-top" />
          </template>
          <fts-select
            slot="formElement"
            slot-scope="{formData, item}"
            :form-data="formData"
            :item="item"
            :options="types"
            comfort-design
          >
            <fts-label-group
              slot="selected-option-container"
              slot-scope="{ option }"
              :item-id="option.id"
              :list="types"
            />
            <fts-label-group
              slot="option"
              slot-scope="{ option }"
              :item-id="option.id"
              :list="types"
            />
          </fts-select>
        </fts-form-element>
        <!-- url -->
        <fts-form-element
          :form-data="formData"
          :item="{
            displayName: 'URL',
            fieldName: 'url',
            type: 33
          }"
          comfort-design
        >
          <template #formLabel="{ label }">
            {{label}} <fts-release-badge alpha class="fts-align-top" />
          </template>
        </fts-form-element>
        <!-- new tab (WIP) -->
        <fts-form-element
          :form-data="formData"
          :item="{
            displayName: 'New Tab',
            fieldName: 'newTab',
            input: 'toggleSwitch'
          }"
          comfort-design
          v-if="formData.type === 'goToUrl' && isDevelopment"
        >
          <template #formLabel="{ label }">
            {{label}} <fts-release-badge dev class="fts-align-top" />
          </template>
        </fts-form-element>
      </template>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth -->
      <fts-auth
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)" v-if="Boolean(saveCallBack)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import Swatches from "vue-swatches";
import changesets from "diff-json";

import validator from "../commons/designValidator";
import { darkPalette } from "../commons/colors";
import { authorizationStore } from '../store/authorizationStore';

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSEmoji from "../elements/FTSEmoji";
import FTSScriptEditor from "./FTSScriptEditor";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSContenteditable from "../elements/FTSContenteditable";

export default {
  components: {
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-emoji": FTSEmoji,
    "fts-script-editor": FTSScriptEditor,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-contenteditable": FTSContenteditable
  },
  data: () => ({
    types: [{ id: 'goToUrl', name: 'Go To URL' }, { id: 'customPage', name: 'Custom Page' }],
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
  }),
  validations() {
    let formData = {};
    formData = validator.actionDesign(this.actions);
    return { formData };
  },
  methods: {
    async show(item, saveCallBack) {
      this.saveCallBack = saveCallBack;
      this.formData = _.cloneDeep(item);

      if (!this.formData.script)
        this.$set(this.formData, "script", "");
      if (!this.formData.condition)
        this.$set(this.formData, "condition", "");

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData)
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });             
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
        });
      })
    },
    done(save, enableToast) {
      this.$v.formData.$touch();

      if (this.$v.formData.$invalid) {
        if (enableToast) {
          this.$toasted.error("Configuration error. Please ensure all values are in a proper format.");
        }
        return;
      }

      if (save) {
        this.saveCallBack && this.saveCallBack(this.formData);
        this.originalData = _.cloneDeep(this.formData);
      } else {
        this.resolve && this.resolve(this.formData);
        this.reset();
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$confirmation.show({
        type: 'delete',
        title: 'Custom',
        text: this.formData.name,
        textEmoji: this.formData.emoji,
        textColor: this.formData.color
      });

      if (res === true) {
        this.resolve && this.resolve(true);
        this.reset();
      }
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
    },
    error(key) {
      return validator.actionDesignError(this.$v.formData, key);
    },
    selectColor(val) {
      if (!val) this.$delete(this.formData, 'color');
    },
  },
  computed: {
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    actions() {
      return this.$store.getters.canvasActions;
    },
    menus() {
      return this.$store.getters.canvasMenus.map(x => ({
        id: x.id,
        name: x.name,
        emoji: x.emoji,
      }));
    },
    colors() {
      return darkPalette.concat([""]);
    },
    isExist() {
      return this.actions.some(x => x.id === this.formData.id);
    },
    getStyle() {
      if (this.formData && this.formData.name && this.formData.color)
        return {
          "color": this.formData.color,
        }
    },
    conditionDisabled() {
      return !(authorizationStore.getters.isAdministrator || Boolean(this.formData.condition));
    },
  }
}
</script>